import { type CSSProperties, type ReactNode } from 'react'
import clsx from 'clsx'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

export interface SortableVerticalListProps {
  /** Aktuelle Reihenfolge der IDs (string oder number als String für Kit) */
  itemIds: string[]
  /** Liefert die neue ID-Reihenfolge nach einem Drop */
  onReorder: (nextIds: string[]) => void
  /** Optionaler Klassenname für den Container der Items */
  containerClassName?: string
  children: ReactNode
}

/**
 * Vertikale Sortierliste (@dnd-kit) — gleicher Stack wie Bausteine / Curricula-Listen.
 * Kinder pro Zeile: {@link SortableListItem}
 */
export function SortableVerticalList({ itemIds, onReorder, containerClassName, children }: SortableVerticalListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = itemIds.indexOf(String(active.id))
    const newIndex = itemIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onReorder(arrayMove(itemIds, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className={clsx(containerClassName)}>{children}</div>
      </SortableContext>
    </DndContext>
  )
}

export interface SortableListItemRenderArgs {
  setNodeRef: (element: HTMLElement | null) => void
  /** Transform der Zeile — bei @dnd-kit üblich (Ausnahme zu „keine inline styles“ in ui-rules) */
  style: CSSProperties
  /** Nur auf den Griff legen, nicht auf die ganze Karte */
  dragHandleProps: Record<string, unknown>
  isDragging: boolean
}

export interface SortableListItemProps {
  id: string
  children: (args: SortableListItemRenderArgs) => ReactNode
}

export function SortableListItem({ id, children }: SortableListItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1
  }

  const dragHandleProps = { ...attributes, ...listeners }

  return <>{children({ setNodeRef, style, dragHandleProps, isDragging })}</>
}
